import { Routes } from '@angular/router';
import { RegisterComponent } from './auth/register/register.component';
import { VerifyOtpComponent } from './auth/verify-otp/verify-otp.component';
import { CompleteProfileComponent } from './auth/complete-profile/complete-profile.component';
import { LoginComponent } from './auth/login/login.component';
import { UpdatePasswordComponent } from './auth/update-password/update-password.component';
import { AuthGuard } from './shared/guards/auth.guard';
import { corporateOnlyGuard, nonCorporateGuard } from './shared/guards/profile-type.guard';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { NewPasswordComponent } from './auth/new-password/new-password.component';
import { AuthCallbackComponent } from './auth/auth-callback/auth-callback.component';
import { MagicLinkComponent } from './auth/magic-link/magic-link.component';
import { BookInterviewComponent } from './candidate/Dashboard/dashboard/book-interview/book-interview.component';

export const routes: Routes = [
  // ── Landing pages (lazily loaded) ─────────────────────────────────────────
  {
    path: '',
    title: 'Interpanely | Mock Interviews, Expert Panelists & Hiring Platform',
    data: {
      seo: {
        index: true,
        path: '/',
        description:
          'Interpanely helps candidates prepare with real mock interviews, connects industry experts as panelists, and gives companies a faster, fairer way to screen talent.',
      },
    },
    loadComponent: () =>
      import('./home/candidate-home/candidate-home.component').then(
        (m) => m.CandidateHomeComponent,
      ),
  },
  {
    // Same component/content as '/'. Canonicalised to the root path below to
    // avoid duplicate-content indexing of two identical pages.
    path: 'candidate',
    title: 'Interview. Improve. Grow. | Interpanely for Candidates',
    data: {
      seo: {
        index: true,
        path: '/',
        description:
          'Interpanely helps candidates prepare with real mock interviews, connects industry experts as panelists, and gives companies a faster, fairer way to screen talent.',
      },
    },
    loadComponent: () =>
      import('./home/candidate-home/candidate-home.component').then(
        (m) => m.CandidateHomeComponent,
      ),
  },
  {
    path: 'panel',
    title: 'Evaluate. Earn. Grow. | Become an Panelist on Interpanely',
    data: {
      seo: {
        index: true,
        path: '/panel',
        description:
          'Join Interpanely as an industry expert panelist. Evaluate real candidates, earn based on your expertise, and build flexible income around your schedule.',
      },
    },
    loadComponent: () =>
      import('./home/panel-home/panel-home.component').then((m) => m.PanelHomeComponent),
  },
  {
    path: 'employer',
    title: 'Interpanely for Employers | Faster, Smarter Technical Hiring',
    data: {
      seo: {
        index: true,
        path: '/employer',
        description:
          'Outsource technical screening to Interpanely. Run expert-led mock and real interviews, manage candidate pipelines, and hire faster with fairer, more consistent evaluations.',
      },
    },
    loadComponent: () =>
      import('./home/corporate-home/corporate-home.component').then(
        (m) => m.CorporateHomeComponent,
      ),
  },
  // Legacy URL, redirect old bookmarks/links to the renamed path.
  { path: 'corporate', redirectTo: 'employer', pathMatch: 'full' },

  // Short referral link (`ip.../r/CODE`) -- see ReferralRedirectComponent.
  {
    path: 'r/:code',
    loadComponent: () =>
      import('./auth/referral-redirect/referral-redirect.component').then(
        (m) => m.ReferralRedirectComponent,
      ),
  },

  // ── Auth ──────────────────────────────────────────────────────────────────
  {
    path: 'auth',
    children: [
      { path: 'login', title: 'Log In | Interpanely', component: LoginComponent },
      { path: 'register', title: 'Create an Account | Interpanely', component: RegisterComponent },
      {
        path: 'verify-otp',
        title: 'Verify Your Account | Interpanely',
        component: VerifyOtpComponent,
      },
      {
        path: 'complete-profile',
        title: 'Complete Your Profile | Interpanely',
        component: CompleteProfileComponent,
      },
      {
        path: 'update-password',
        title: 'Update Password | Interpanely',
        component: UpdatePasswordComponent,
      },
      {
        path: 'forgot-password',
        title: 'Forgot Password | Interpanely',
        component: ForgotPasswordComponent,
      },
      {
        path: 'new-password',
        title: 'Set a New Password | Interpanely',
        component: NewPasswordComponent,
      },
      {
        path: 'callback',
        title: 'Signing You In… | Interpanely',
        component: AuthCallbackComponent,
      },
      {
        path: 'magic-link',
        title: 'Magic Link Sign-In | Interpanely',
        component: MagicLinkComponent,
      },
      {
        path: 'phone-login',
        title: 'Phone OTP Login | Interpanely',
        loadComponent: () =>
          import('./auth/phone-login/phone-login.component').then((m) => m.PhoneLoginComponent),
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },

  // PUBLIC: invited candidate booking page, NO AuthGuard
  // Must be ABOVE the guarded app/candidate block.
  // Angular matches routes top-to-bottom; this exact path wins before the
  // lazy-loaded app/candidate block (which has AuthGuard) ever sees it.
  {
    path: 'app/candidate/book-interview',
    title: 'Book Your Interview | Interpanely',
    component: BookInterviewComponent,
  },

  // ── Protected app routes (lazily loaded per role) ─────────────────────────
  {
    path: 'app/candidate',
    canActivate: [AuthGuard, nonCorporateGuard],
    loadChildren: () => import('./candidate/candidate.routes').then((m) => m.CANDIDATE_ROUTES),
  },
  {
    path: 'app/employer',
    canActivate: [AuthGuard, corporateOnlyGuard],
    loadChildren: () => import('./corporate/corporate.routes').then((m) => m.CORPORATE_ROUTES),
  },
  // Legacy URL, redirects the old dashboard root only (deep links to old
  // /app/corporate/* sub-pages fall through to the 404 route below, since
  // Angular can't forward arbitrary trailing segments through a redirect).
  { path: 'app/corporate', redirectTo: 'app/employer', pathMatch: 'full' },
  {
    path: 'app/panelist',
    canActivate: [AuthGuard, nonCorporateGuard],
    loadChildren: () => import('./panelist/panelist.routes').then((m) => m.PANELIST_ROUTES),
  },

  // ── FAQ (lazily loaded) ───────────────────────────────────────────────────
  {
    path: 'faq',
    title: 'Frequently Asked Questions | Interpanely',
    data: {
      seo: {
        index: true,
        path: '/faq',
        description:
          'Answers to common questions about creating a candidate account, booking mock interviews, employer hiring pipelines, and joining Interpanely as an panelist.',
      },
    },
    loadComponent: () => import('./home/faq/faq.component').then((m) => m.FaqComponent),
  },

  // ── Help pages (lazily loaded) ────────────────────────────────────────────
  {
    path: 'help',
    loadChildren: () => import('./home/help/help.routes').then((m) => m.HELP_ROUTES),
  },

  // ── Legal pages (lazily loaded) ───────────────────────────────────────────
  {
    path: 'legal',
    loadChildren: () => import('./home/legal/legal.routes').then((m) => m.LEGAL_ROUTES),
  },

  // ── Auditor portal (lazily loaded) ────────────────────────────────────────
  {
    path: 'auditor',
    loadChildren: () => import('./auditor/auditor.routes').then((m) => m.AUDITOR_ROUTES),
  },

  // ── 404 ───────────────────────────────────────────────────────────────────
  {
    path: '**',
    title: 'Page Not Found | Interpanely',
    loadComponent: () =>
      import('./shared/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
];
